import type { CampaignChannel } from '../../api/types';

/**
 * "Try a test campaign": the builder walked once, for real, to an audience of
 * one — the person doing it.
 *
 * The builder has no test send, and that is deliberate (the review step's
 * rendered preview is the pre-flight). What it does have is **Your own list**,
 * so the honest test is a real campaign whose whole list is your own address:
 * it goes through the same mailbox, the same merge, the same footer and the
 * same tracked links as the real one, and its Results fill in as it is opened
 * and clicked. This file is the script for that walk — which control each
 * moment points at, what it says, and the one button a moment may offer —
 * kept pure so the cases that change it (a text campaign, no address on file,
 * no AI key, a Draft-level author) are tested without a screen.
 */

export type TourActionKind = 'useMyAddress' | 'nameIt' | 'setUpAi' | 'connect';

export interface TourMoment {
  key: string;
  /** The builder step this moment lives on — the walk moves the builder there. */
  step: 1 | 2 | 3 | 4;
  /** `data-tour` of the control to ring. */
  target: string;
  title: string;
  /** Paragraphs; `**bold**` is the only markup. */
  body: string[];
  action?: { kind: TourActionKind; label: string; unavailable?: string };
  /** Said when the control is not on screen, so the card is never a dead end. */
  missing?: string;
}

export interface TourContext {
  channel: CampaignChannel;
  /** The address the test goes to — the agent's own email, or phone on a text campaign. */
  address: string | null;
  /** Whether an AI key resolves; null while that is still being asked. */
  aiReady: boolean | null;
  /** Whether this person may open AI settings and add a key (AI at Manage). */
  mayManageAi: boolean;
  /** Campaigns at Send — otherwise the launch button submits for review. */
  mayApprove: boolean;
  /** AI assist is offered at all — AI is not off for this person. */
  aiOffered: boolean;
  /** A mailbox (or SMS number) this person can send from exists on the chosen channel. */
  hasSender: boolean;
  /** Whether this person may connect one (Connect channels). */
  mayConnect: boolean;
}

export function campaignTourMoments(ctx: TourContext): TourMoment[] {
  const sms = ctx.channel === 'sms';
  const noun = sms ? 'text' : 'email';
  const sender = sms ? 'number' : 'mailbox';
  const moments: TourMoment[] = [
    {
      key: 'channel',
      step: 1,
      target: 'channel',
      title: 'Test it the way it will really go',
      body: [
        `A test campaign is a real one with a list of one — you. Pick the channel the real campaign will use, so the test goes through the same ${sms ? 'number' : 'mailbox'}, the same footer and the same links.`,
      ],
    },
    // Said straight after the channel, while there is still nothing typed to
    // lose: without a sender the walk can teach the builder but cannot end in a
    // send, and finding that out on the last card is the worse way round.
    ...(ctx.hasSender
      ? []
      : [
          {
            key: 'connect',
            step: 1 as const,
            target: 'no-sender',
            title: `Connect a ${sender} first`,
            body: [
              `A test goes out through the same ${sender} the real campaign will, and there is none to send from yet — so as it stands this campaign can be built, but not saved or sent.`,
              ctx.mayConnect
                ? `**Connect a ${sender}** opens in a new tab. Connect it, come back, press **Check again**, and carry on from here.`
                : 'Connecting one needs an administrator. Carry on to see how the builder works, and send the test once they have.',
            ],
            action: ctx.mayConnect
              ? { kind: 'connect' as const, label: sms ? 'Connect a number' : 'Connect a mailbox' }
              : undefined,
          },
        ]),
    {
      key: 'audience',
      step: 1,
      target: 'audience-custom',
      title: 'Send it to yourself',
      body: [
        '**Your own list** is a list you type or upload instead of one drawn from dispatch — so the test reaches nobody but you.',
      ],
      action: {
        kind: 'useMyAddress',
        label: ctx.address ? `Use ${ctx.address}` : 'Use my address',
        unavailable: ctx.address
          ? undefined
          : sms
            ? 'Your dispatch profile has no phone number — pick Your own list and type your number.'
            : 'We do not have your email address — pick Your own list and type it.',
      },
    },
    {
      key: 'list',
      step: 1,
      target: 'custom-list',
      title: 'A list of one',
      body: [
        `${sms ? 'Your number' : 'Your address'} is the whole list. To show it to a colleague too, add them on the next line — ${sms ? 'number' : 'email'} first, then their name.`,
      ],
      missing: 'Pick **Your own list** above and this box appears.',
    },
    {
      key: 'name',
      step: 2,
      target: 'message-name',
      title: 'Call it a test',
      body: ['It is listed with every other campaign, so a name that says "test" saves somebody wondering later.'],
      action: { kind: 'nameIt', label: 'Name it "Test campaign"' },
    },
    {
      key: 'body',
      step: 2,
      target: 'message-body',
      title: `Write the ${noun}`,
      body: [
        sms
          ? 'Keep it short — the count under the box is what one text costs. Data points such as **{{name}}** fill in per recipient; on your test, with your own name.'
          : 'Data points such as **{{name}}** fill in per recipient — on your test, with your own name. The preview further down shows it as it will arrive.',
      ],
    },
  ];

  if (ctx.aiOffered) {
    moments.push({
      key: 'ai',
      step: 2,
      target: 'ai-assist',
      title: ctx.aiReady === false ? 'AI can draft it — once it has a key' : 'Or let AI draft it',
      body:
        ctx.aiReady === false
          ? [
              'AI assist writes from a one-line brief and polishes what you have written, but it needs an AI key first — your OpenAI or Anthropic key, added once in **AI settings**.',
              ctx.mayManageAi
                ? 'It opens in a new tab, so this campaign stays as it is. Come back and press **Check again**.'
                : 'Adding one needs AI at Manage — ask whoever looks after your CRM settings.',
            ]
          : [
              'Describe it in a line and press **Generate** for a fresh draft, or **Improve** to polish what you have. It knows your company details and uses data points rather than typing them in.',
            ],
      action:
        ctx.aiReady === false && ctx.mayManageAi ? { kind: 'setUpAi', label: 'Set up AI' } : undefined,
    });
  }

  if (!sms) {
    moments.push({
      key: 'tracking',
      step: 2,
      target: 'tracking',
      title: 'Links that report back',
      body: [
        'Links to your own website carry this campaign’s tracking name, so even your test click is counted — it shows on the campaign’s Results as a visit.',
      ],
    });
  }

  moments.push(
    {
      key: 'pace',
      step: 3,
      target: 'throttle',
      title: 'How fast it goes out',
      body: [
        `How many ${sms ? 'texts' : 'emails'} a minute. A list of one goes at once — this is the setting that matters when the real list is thousands.`,
      ],
    },
    {
      key: 'review',
      step: 4,
      target: 'review-checks',
      title: 'The pre-flight review',
      body: [
        'Checks on who it is sent from and who gets it, and an AI read of the words (marked AI). Amber is worth a look, red needs fixing — each row says what to do, with a button beside it.',
      ],
    },
    {
      key: 'send',
      step: 4,
      target: 'launch',
      title: ctx.mayApprove ? 'Send it to yourself' : 'Send it for review',
      body: !ctx.hasSender
        ? [
            `This is where it would go out — and it stays greyed until a ${sender} is connected, as does saving. Connect one in its new tab, press **Check again** at the top of this screen, and the send is one press: this campaign stays as it is meanwhile.`,
          ]
        : ctx.mayApprove
        ? [
            `**Send campaign** ${sms ? 'texts' : 'mails'} your list — just you — within seconds. Open it, ${sms ? 'check how it reads on your phone' : 'click a link'}, then come back to its Results and watch it counted.`,
          ]
        : [
            '**Send for review** passes it to whoever approves campaigns. Once they send it, it arrives — and its Results fill in the same way.',
          ],
    },
  );
  return moments;
}

/** `**bold**` → segments, the one piece of markup a moment's body carries. */
export function boldSegments(text: string): { text: string; bold: boolean }[] {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part) =>
      part.startsWith('**') && part.endsWith('**')
        ? { text: part.slice(2, -2), bold: true }
        : { text: part, bold: false },
    );
}
